/**
 * App Store Listings (W13 post-approval mgmt, P2) — the MOD MANAGEMENT TABLE view
 * model (pure, React-free). The per-row lifecycle ACTION SET, computed from a
 * listing's `status` + `kind` (+ whether a pending publish request exists), plus
 * the human labels. Extracted so the correctness gate lives in the node
 * `unit` project, mirroring the
 * sibling `appListingModerationView` (the report-queue view model).
 *
 * KIND-AWARENESS (from the merged Phase 1 procs + the #3165 onsite reset backend):
 *   - `reset-to-pending` is DUAL-KIND — off-site routes through
 *     `resetListingToPending`, on-site through `resetOnsiteListingToPending` (which
 *     suspends the backing block + re-queues the block review). The caller routes by
 *     kind; the action is offered for an approved listing of EITHER kind.
 *   - `claim` is OFF-SITE ONLY (the service raises NOT_FOUND for an on-site listing).
 *   - `purge` is OFF-SITE at `removed`, PLUS one on-site shape: an ORPHAN PRE-APPROVAL
 *     DRAFT (never approved, not a shadow, no live block request). That arm exists
 *     because `rejectRequest` no longer deletes the draft (clawgate #302), so it is the
 *     only way to reclaim an abandoned submission's slug. Every OTHER on-site listing
 *     still raises NOT_FOUND.
 *   - `hide` (delist) / `relist` are DUAL-KIND (they flip the on-site AppBlock too).
 *   - `review` opens the existing off-site review modal (approve/reject the pending
 *     request) → off-site only, and only when a pending request exists.
 */

import { LISTING_KIND_LABELS } from '~/components/Apps/listingKindLabels';
import type { ModerationListingRow } from '~/server/services/blocks/app-listing.service';
import { isVisibilityEligibleListingStatus } from '~/shared/utils/app-listing-visibility';

/**
 * The DISPLAY status for the mod management table.
 *
 * An EXTERNAL listing awaiting its FIRST review is stored (atomically, by the
 * submit path) as an AppListing with `status='draft'` PLUS a live pending
 * AppListingPublishRequest. Its raw `status` reads 'draft', yet the submitter's
 * own my-submissions surface shows 'pending' (the request status) — so the two
 * surfaces disagree, and a mod filtering the mgmt table by "Pending" does NOT
 * see the item that most needs reviewing. Treat such a draft-with-a-live-pending
 * -request as EFFECTIVELY pending for the mod table's DISPLAY (bucket + badge)
 * and its status filter, so both agree on "awaiting first review".
 *
 * DISPLAY-ONLY: action availability still keys off the REAL `row.status` (the
 * caller passes `hasPendingRequest` to {@link listingModActions} separately).
 * Total — never throws.
 */
export function effectiveModerationStatus(
  row: Pick<ModerationListingRow, 'status' | 'pendingRequest'>
): string {
  return row.status === 'draft' && row.pendingRequest != null ? 'pending' : row.status;
}

/** The lifecycle actions a mod row can offer (a subset renders per row). */
export type ListingModAction =
  | 'review'
  | 'message-owner'
  | 'reset-to-pending'
  | 'hide'
  | 'relist'
  | 'claim'
  | 'purge'
  | 'set-visibility';

/**
 * The ordered set of actions to render for a listing row.
 *
 *   - `review`: off-site + a pending publish request exists (any status — normally
 *     a `pending` listing, but a lingering pending request on another status still
 *     lets a mod open the review). Opens the reused off-site review modal.
 *   - `message-owner`: EVERY row, EVERY status, BOTH kinds. See below.
 *   - `approved` → `reset-to-pending` (dual-kind — off-site + on-site re-queue) +
 *     `hide` (delist, dual-kind).
 *   - `removed`  → `relist` (dual-kind) + `claim` + `purge` (both off-site only here).
 *   - `draft` → `purge` for an ON-SITE orphan pre-approval draft (never approved, no live
 *     block request); otherwise no LIFECYCLE action beyond `message-owner`, unless a
 *     pending request makes `review` available.
 *   - `rejected` → no LIFECYCLE action beyond `message-owner`.
 *
 * 🔴 `message-owner` IS UNCONDITIONAL, and that is a claim about the SERVER, not a
 * preference. `appListings.messageAppOwner` resolves its recipient through
 * `resolveListingAccess`, which branches on KIND and never on STATUS — so there is no
 * listing in this table the proc would refuse. Narrowing the button to (say) approved
 * rows would withhold it in exactly the states where a moderator most needs it: a
 * `rejected` or `draft` row is one a developer is being asked to FIX, and before this
 * change those rows rendered a dead `—` with no way to tell them what to fix.
 *
 * 🔴 It is placed AFTER `review` and BEFORE the lifecycle actions so the row reads in
 * increasing severity (Review → Message owner → Reset → Hide → … → Purge) and the
 * destructive `purge` stays rightmost. Do not move it after `purge`: the rendering
 * order in `AppListingsModerationTable` is this array's order, and a benign button to
 * the right of a red irreversible one is a misclick waiting to happen.
 */
export function listingModActions(input: {
  status: string;
  kind: string;
  hasPendingRequest: boolean;
  /** `null` ⇒ never approved. Only used to identify an on-site orphan draft (see below). */
  appBlockId: string | null;
  /**
   * A live `AppBlockPublishRequest` for this slug — the ON-SITE "under review" signal, which
   * `hasPendingRequest` structurally cannot carry. See the 🔴 note at the purge branch.
   */
  hasPendingBlockRequest: boolean;
}): ListingModAction[] {
  const offsite = input.kind === 'offsite';
  const actions: ListingModAction[] = [];

  // Review is available whenever there's a pending request to act on (off-site).
  if (offsite && input.hasPendingRequest) actions.push('review');

  // Messaging the owner is state-neutral and dual-kind — always offered.
  actions.push('message-owner');

  if (input.status === 'approved') {
    // Reset-to-pending is now dual-kind: off-site → resetListingToPending, on-site →
    // resetOnsiteListingToPending (#3165). The mgmt table routes by kind.
    actions.push('reset-to-pending');
    actions.push('hide'); // delist — dual-kind
  }
  if (input.status === 'removed') {
    actions.push('relist'); // dual-kind
    if (offsite) {
      actions.push('claim');
      actions.push('purge');
    }
  }

  /**
   * 🔴 THE LEVEL CONTROL — OFFERED ON EXACTLY THE STATUSES THE SERVER WILL ACCEPT, asked
   * via the SHARED predicate rather than a local status list. `applyVisibility` refuses
   * anything outside `VISIBILITY_ELIGIBLE_LISTING_STATUSES` for a moderator too, so a
   * second spelling here would drift into offering a button that can only fail — the shape
   * the `mod-removed` Republish cell exists to forbid on the owner surface.
   *
   * Dual-kind: the level is a property of the LISTING row, and an on-site app's store
   * discoverability is governed by the same column, so there is no `offsite` term.
   *
   * 🔴 IT SITS BEFORE THE PURGE BRANCH SO **PURGE STAYS RIGHTMOST**, and that ordering is
   * an invariant with its own test ("is never the LAST action when a destructive one is
   * present"), not a cosmetic preference. An earlier draft of this block appended after
   * purge, which read as harmless — every status that offers purge except ONE also
   * withholds the level control. The exception is the on-site orphan pre-approval DRAFT,
   * which is level-eligible AND purgeable, so appending moved a hard-delete button out from
   * under the moderator's cursor and put a benign one there instead. The invariant test is
   * what caught it; the fix is the position, not the assertion.
   */
  if (isVisibilityEligibleListingStatus(input.status)) {
    actions.push('set-visibility');
  }

  // 🔴 ON-SITE ORPHAN PRE-APPROVAL DRAFT — the ONLY on-site shape `purgeListing` accepts, and
  // the only way a moderator can reclaim its slug.
  //
  // A `rejectRequest` no longer deletes this row (clawgate #302), so a rejected-and-abandoned
  // first submission sits here holding `app_listings_slug_key` forever. `delistListing` is
  // status-guarded to `{approved, removed}` and cannot touch a `draft`, so WITHOUT this branch
  // the row is visible in this table with no action but "message owner" — which is exactly the
  // state the reject-time delete used to prevent. The service arm existing is not enough; the
  // operator has to be able to reach it.
  //
  // The terms mirror the service. `appBlockId === null` means never approved. Shadow
  // revisions need no term — `listAllListingsForModeration` already filters
  // `revisionOfId: null`, so one can never be a row in this table.
  //
  // 🔴 "NOT UNDER REVIEW" READS `hasPendingBlockRequest`, **NOT** `hasPendingRequest`, and the
  // difference is the whole guard. `hasPendingRequest` is derived from the
  // `AppListingPublishRequest` relation, whose `appListingId` is "On-site: NULL until approve"
  // — so for an on-site pre-approval draft it is ALWAYS false, and gating on it offered Purge
  // on submissions that were actively under review. The live submission behind such a row is
  // an `AppBlockPublishRequest` joined by SLUG, which is what `hasPendingBlockRequest` carries.
  //
  // 🔴 IT STAYS LAST so the destructive button keeps the rightmost position — see the level
  // control's note above for the one row where that genuinely collided.
  if (
    !offsite &&
    input.status === 'draft' &&
    input.appBlockId === null &&
    !input.hasPendingBlockRequest
  ) {
    actions.push('purge');
  }
  return actions;
}

/**
 * 🔴 THE ROW-SHAPED ENTRY POINT, and the reason it exists is testability, not convenience.
 *
 * The normalization below used to live inline in `AppListingsModerationTable.tsx`. Nothing could
 * test it there: the browser tier is the only suite that renders that component, it has no
 * on-site orphan-draft fixture, and (on this host) it cannot run at all. Measured — inverting
 * the default to the PERMISSIVE direction left the entire unit tier, 1601 files and 25,069
 * tests, green. A guard nothing can kill is not a guard.
 *
 * Moving it here makes it a pure function the unit tier reaches, so the mutation dies.
 *
 * 🔴 ABSENT MEANS "ASSUME UNDER REVIEW". `hasPendingBlockRequest` is newer than some clients
 * that will call this — a bundle from either side of a deploy, or any future producer of the
 * DTO — and `!undefined` is truthy, i.e. the permissive direction on the input to a DESTRUCTIVE
 * affordance. Defaulting to `true` withholds Purge when we cannot tell, which is the only safe
 * direction: the cost of a wrongly-withheld Purge is a moderator retrying after a refresh; the
 * cost of a wrongly-offered one is a hard delete of a listing under live review.
 */
export function listingModActionsForRow(row: {
  status: string;
  kind: string;
  appBlockId: string | null;
  pendingRequest: unknown | null;
  hasPendingBlockRequest?: boolean | null;
}): ListingModAction[] {
  return listingModActions({
    status: row.status,
    kind: row.kind,
    hasPendingRequest: row.pendingRequest != null,
    appBlockId: row.appBlockId,
    hasPendingBlockRequest: row.hasPendingBlockRequest ?? true,
  });
}

/** Whether an action is the destructive one that must be confirmed before firing. */
export function isDestructiveListingModAction(action: ListingModAction): boolean {
  return action === 'purge';
}

/** Which modal a mod action opens. Three routes, and every action names exactly one. */
type ListingModRoute =
  /** The reused off-site publish-request review modal. */
  | 'review'
  /** `MessageAppOwnerModal` — subject + body, no `reason`. */
  | 'owner-message'
  /** The shared `ListingModActionModal` — one `reason` at `OFFSITE_MOD_REASON_MIN`. */
  | 'reason'
  /**
   * `ModListingVisibilityModal` — a LEVEL plus a `reason`.
   *
   * 🔴 A FOURTH ROUTE RATHER THAN REUSING `reason`, because the shared reason-gated modal
   * has exactly one free-text field and this act needs a second input the moderator must
   * CHOOSE. Routing it to `reason` would have made `actionRequiresReason` read "shows a
   * reason textarea" for a surface that shows a radio group as well — the same overclaim
   * the `message-owner` entry above was separated out to avoid, and the table is
   * single-valued precisely so that is unrepresentable.
   */
  | 'visibility';

/**
 * 🔴 THE ROUTING TABLE, AND THE REASON IT IS A TABLE RATHER THAN TWO PREDICATES.
 *
 * `AppListingsModerationTable.openAction` tries `review`, then
 * {@link actionOpensOwnerMessage}, then {@link actionRequiresReason}, and an action
 * claimed by none of the three opens NOTHING. That "jointly total" property is what the
 * table's own comment leans on — but while `actionRequiresReason` was written as a
 * NEGATION (`action !== 'review' && action !== 'message-owner'`) the property was
 * decorative: a member added to {@link ListingModAction} answered `true` by DEFAULT and
 * landed in the reason-gated modal, which is precisely the quiet mis-route the comment
 * claimed to prevent. Measured before this table existed: adding a member to the union
 * left all 46 unit tests green, and the only objection came from
 * {@link listingModActionLabel}'s exhaustive switch — the LABEL half, not the routing
 * half.
 *
 * An exhaustive `Record<ListingModAction, …>` moves the claim into the type system: a
 * new member is a MISSING PROPERTY here (`pnpm typecheck`, a blocking check) rather than
 * a silent default, and at runtime an unlisted action resolves to `undefined`, so both
 * predicates answer `false` and the action opens nothing — the loud direction, which is
 * what the comment always said and now describes.
 *
 * 🔴 "EVERY ACTION NAMES EXACTLY ONE ROUTE" IS TRUE OF THIS TABLE, NOT OF THE DISPATCH.
 * The `review` value is read by nothing: `AppListingsModerationTable.openAction` and its
 * test both branch on the literal `action === 'review'` before either predicate runs. So
 * a NEW member mapped here to `'review'` typechecks cleanly and opens nothing at all —
 * caught, but by the jointly-total sweep in `appListingModerationTableView.test.ts` (a
 * blocking test), not by the type system this docstring credits above. Wiring the review
 * branch through the table would close that; until it is, the row is documentation of the
 * third route rather than the thing that selects it.
 *
 * 🔴 `message-owner` routes to the composer, NOT to `reason`, and that is not an
 * oversight. It carries no `reason` at all: `appListings.messageAppOwner` takes a
 * SUBJECT and a BODY with their own, different floors (`MOD_MESSAGE_SUBJECT_MIN` /
 * `MOD_MESSAGE_BODY_MIN`). Routing it to `reason` would leave that predicate reading
 * "shows a reason textarea" for a surface that shows none, and — because a table entry
 * is single-valued — is now unrepresentable rather than merely discouraged.
 */
const LISTING_MOD_ROUTES: Record<ListingModAction, ListingModRoute> = {
  review: 'review',
  'message-owner': 'owner-message',
  'reset-to-pending': 'reason',
  hide: 'reason',
  relist: 'reason',
  claim: 'reason',
  purge: 'reason',
  'set-visibility': 'visibility',
};

/**
 * Whether an action opens the LEVEL picker (a visibility level + a `reason`).
 *
 * Disjoint from the other two predicates by construction — one table entry per action.
 */
export function actionOpensVisibility(action: ListingModAction): boolean {
  return LISTING_MOD_ROUTES[action] === 'visibility';
}

/**
 * Every member of {@link ListingModAction}, derived from {@link LISTING_MOD_ROUTES}
 * rather than hand-listed, so the vocabulary has ONE spelling. Iterated by
 * `appListingModerationTableView.test.ts` for its label/route totality sweeps — a
 * hand-maintained copy there could silently stop covering a member the union gained.
 */
export const ALL_LISTING_MOD_ACTIONS = Object.keys(LISTING_MOD_ROUTES) as ListingModAction[];

/**
 * Whether an action opens the shared REASON-gated modal (`ListingModActionModal`,
 * whose one free-text field is `reason` and whose floor is `OFFSITE_MOD_REASON_MIN`).
 *
 * Pinned in two places: the vocabulary in `appListingModerationTableView.test.ts`, and
 * that the table still CALLS this at all in
 * `__tests__/appModeratorMessageForm.callSites.test.ts` — this predicate spent one
 * revision referenced by nothing but its own test, which is the shape that lets a
 * "🔴 routing depends on this" comment describe dead code.
 */
export function actionRequiresReason(action: ListingModAction): boolean {
  return LISTING_MOD_ROUTES[action] === 'reason';
}

/**
 * Whether an action routes to `MessageAppOwnerModal` rather than the shared
 * reason-gated one. Disjoint from `actionRequiresReason` BY CONSTRUCTION — one table
 * entry per action — where the two used to be independent predicates that merely
 * happened to disagree on every member.
 */
export function actionOpensOwnerMessage(action: ListingModAction): boolean {
  return LISTING_MOD_ROUTES[action] === 'owner-message';
}

/** Human label for a mod action button. */
export function listingModActionLabel(action: ListingModAction): string {
  switch (action) {
    case 'review':
      return 'Review';
    case 'message-owner':
      return 'Message owner';
    case 'reset-to-pending':
      return 'Reset to pending';
    case 'hide':
      return 'Hide';
    case 'relist':
      return 'Relist';
    case 'claim':
      return 'Claim';
    case 'purge':
      return 'Purge';
    case 'set-visibility':
      return 'Visibility';
  }
}

/** Chip descriptor (label + Mantine color name) for a listing's store status. */
export type ListingKindChip = { label: string; color: string };

/** Chip for a listing's `kind` (the per-row kind badge). */
export function listingKindChip(kind: string): ListingKindChip {
  return kind === 'offsite'
    ? { label: LISTING_KIND_LABELS.offsite, color: 'grape' }
    : { label: LISTING_KIND_LABELS.onsite, color: 'blue' };
}
